// Shared utilities for the global keyboard-shortcut handler.

/**
 * Returns true when focus is in a text-editing surface — input,
 * textarea, contenteditable, or a Radix Dialog where typing is the
 * primary interaction. Used by global shortcuts to bow out so the
 * user can type plain text without triggering navigation chords.
 */
export function isTypingInInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Cmd on macOS, Ctrl elsewhere. The bag of keyboard meta+key combos
 * we want to feel native on both. Pass an event from a keydown
 * listener; returns true if cmd-or-ctrl is held but ALT and SHIFT are
 * not (we don't want to swallow OS-level shortcuts that include
 * those).
 */
export function isPrimaryModifier(e: KeyboardEvent): boolean {
  return (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey;
}

/**
 * Chord tracker for Linear-style two-key sequences (e.g. "g s" → go
 * schedule). Starts a 1-second window after the first key; returns
 * true while the window is open. Callers consult `peek()` from their
 * own keydown handler when the *second* key fires.
 *
 * Single Map keyed by a stable token so multiple call sites share the
 * same chord state.
 */
export interface ChordTracker {
  arm(key: string): void;
  /** Returns the armed key if the window is still open, or null. */
  peek(): string | null;
  disarm(): void;
}

export function createChordTracker(windowMs = 1000): ChordTracker {
  let armed: string | null = null;
  let handle: number | null = null;
  return {
    arm(key) {
      armed = key;
      if (handle !== null) window.clearTimeout(handle);
      handle = window.setTimeout(() => {
        armed = null;
        handle = null;
      }, windowMs);
    },
    peek() {
      return armed;
    },
    disarm() {
      armed = null;
      if (handle !== null) {
        window.clearTimeout(handle);
        handle = null;
      }
    },
  };
}
